import { useState, useEffect, useRef } from 'react';
import { useI18n } from '@/lib/i18n';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash, CalendarBlank, Repeat, X, UserCircle } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { ko as koLocale } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '@/lib/api';

const PRIORITIES = [
  { value: 0, label: 'task.none', color: '' },
  { value: 1, label: 'task.low', color: 'bg-green-500' },
  { value: 2, label: 'task.medium', color: 'bg-yellow-500' },
  { value: 3, label: 'task.high', color: 'bg-red-500' },
];

const FREQ_OPTIONS = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];

export default function TaskEditor({ open, onClose, task, lists, tags, users = [], onSaved }) {
  const { t, lang } = useI18n();
  const dfnsLocale = lang === 'ko' ? koLocale : undefined;
  const [form, setForm] = useState({
    title: '', description: '', list_ids: [], priority: 0,
    tags: [], due_datetime: null, duration_minutes: 30, is_reference: false, recurrence_rule: null, assignees: [],
  });
  const [durUnit, setDurUnit] = useState(1); // minutes per unit: 1, 60, or 1440
  // For an existing task subtask ops hit the API immediately (children are
  // real tasks now); for a new task they're sent embedded on POST.
  const [subs, setSubs] = useState([]);
  const [newSubtask, setNewSubtask] = useState('');
  const [showRecurrence, setShowRecurrence] = useState(false);
  const [recForm, setRecForm] = useState({ freq: 'DAILY', interval: 1, byday: [], until: null, count: null });
  const [tagSearch, setTagSearch] = useState('');
  const tagSearchRef = useRef(null);
  const [boards, setBoards] = useState([]);
  const [columns, setColumns] = useState([]);
  const [placements, setPlacements] = useState([]);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || '', description: task.description || '',
        list_ids: task.list_ids || (task.list_id ? [task.list_id] : []),
        priority: task.priority || 0, tags: task.tags || [],
        due_datetime: task.due_datetime || null,
        duration_minutes: task.duration_minutes ?? 30,
        is_reference: !!task.is_reference,
        recurrence_rule: task.recurrence_rule || null,
        assignees: task.assignees || (task.assigned_to ? [task.assigned_to] : []),
      });
      const dm = task.duration_minutes ?? 30;
      setDurUnit(dm >= 1440 && dm % 1440 === 0 ? 1440 : dm >= 60 && dm % 60 === 0 ? 60 : 1);
      setSubs(task.subtasks || []);
      if (task.recurrence_rule) {
        setRecForm({ ...task.recurrence_rule, byday: task.recurrence_rule.byday || [], until: task.recurrence_rule.until || null });
        setShowRecurrence(true);
      } else {
        setShowRecurrence(false);
        setRecForm({ freq: 'DAILY', interval: 1, byday: [], until: null, count: null });
      }
    } else {
      setForm({ title: '', description: '', list_ids: lists?.[0]?.id ? [lists[0].id] : [], priority: 0, tags: [], due_datetime: null, duration_minutes: 30, is_reference: false, recurrence_rule: null, assignees: [] });
      setDurUnit(1);
      setSubs([]);
      setShowRecurrence(false);
    }
    setTagSearch('');
  }, [task, lists, open]);

  useEffect(() => {
    if (!open || !task?.id) { setPlacements([]); return; }
    Promise.all([
      api.get('/kanban-boards'),
      api.get('/kanban-columns'),
      api.get('/kanban-placements', { params: { task_id: task.id } }),
    ]).then(([b, c, p]) => {
      setBoards(b.data); setColumns(c.data); setPlacements(p.data);
    }).catch(() => {});
  }, [open, task?.id]);

  const addToBoard = async (boardId) => {
    const firstCol = columns.find(c => c.board_id === boardId);
    if (!firstCol) { toast.error(t('common.error')); return; }
    try {
      const { data } = await api.post('/kanban-placements', { task_id: task.id, board_id: boardId, column_id: firstCol.id, order: 0 });
      setPlacements(p => [...p, data]);
    } catch { toast.error(t('common.error')); }
  };

  const removePlacement = async (placement) => {
    setPlacements(p => p.filter(x => x.id !== placement.id));
    try { await api.delete(`/kanban-placements/${placement.id}`); }
    catch { setPlacements(p => [...p, placement]); toast.error(t('common.error')); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    const payload = { ...form, recurrence_rule: showRecurrence ? recForm : null };
    try {
      let saved;
      if (task?.id) {
        saved = (await api.put(`/tasks/${task.id}`, payload)).data;
        saved.subtasks = subs;
      } else {
        saved = (await api.post('/tasks', { ...payload, subtasks: subs })).data;
        saved.subtasks = subs;
      }
      onSaved?.(saved);
      onClose();
    } catch {
      toast.error(t('common.error'));
    }
  };

  const addSubtask = async () => {
    const title = newSubtask.trim();
    if (!title) return;
    setNewSubtask('');
    if (task?.id) {
      try {
        const { data } = await api.post('/tasks', { title, parent_id: task.id });
        setSubs(s => [...s, { id: data.id, title, completed: false }]);
      } catch { toast.error(t('common.error')); }
    } else {
      setSubs(s => [...s, { id: `tmp_${Date.now()}`, title, completed: false }]);
    }
  };

  const toggleSubtask = async (idx) => {
    const sub = subs[idx];
    setSubs(s => s.map((x, i) => i === idx ? { ...x, completed: !x.completed } : x));
    if (task?.id && !String(sub.id).startsWith('tmp_')) {
      try {
        await api.post(`/tasks/${sub.id}/${sub.completed ? 'uncomplete' : 'complete'}`);
      } catch {
        setSubs(s => s.map((x, i) => i === idx ? { ...x, completed: sub.completed } : x));
        toast.error(t('common.error'));
      }
    }
  };

  const removeSubtask = async (idx) => {
    const sub = subs[idx];
    setSubs(s => s.filter((_, i) => i !== idx));
    if (task?.id && !String(sub.id).startsWith('tmp_')) {
      try { await api.delete(`/tasks/${sub.id}`); }
      catch { setSubs(s => [...s.slice(0, idx), sub, ...s.slice(idx)]); toast.error(t('common.error')); }
    }
  };

  const toggleList = (listId) => {
    setForm(f => ({
      ...f, list_ids: f.list_ids.includes(listId) ? f.list_ids.filter(l => l !== listId) : [...f.list_ids, listId]
    }));
  };

  const toggleAssignee = (userId) => {
    setForm(f => ({
      ...f, assignees: f.assignees.includes(userId) ? f.assignees.filter(u => u !== userId) : [...f.assignees, userId]
    }));
  };

  const toggleTag = (tagId) => {
    setForm(f => ({
      ...f, tags: f.tags.includes(tagId) ? f.tags.filter(t => t !== tagId) : [...f.tags, tagId]
    }));
  };

  const toggleDay = (day) => {
    setRecForm(r => ({
      ...r, byday: r.byday.includes(day) ? r.byday.filter(d => d !== day) : [...r.byday, day]
    }));
  };

  const filteredTags = tagSearch.trim()
    ? (tags || []).filter(tg => tg.name.toLowerCase().includes(tagSearch.toLowerCase()))
    : (tags || []);

  const dueDate = form.due_datetime ? parseISO(form.due_datetime) : null;
  const untilDate = recForm.until ? parseISO(recForm.until) : null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="task-editor">
        <SheetHeader>
          <SheetTitle className="font-heading">{task?.id ? t('task.edit') : t('task.add')}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <Input data-testid="task-title-input" placeholder={t('task.title')} value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
          <Textarea data-testid="task-desc-input" placeholder={t('task.description')} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />

          {(lists?.length > 0) && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('task.list')}</label>
              <div className="flex flex-wrap gap-1.5" data-testid="task-list-select">
                {lists.map(l => (
                  <Badge key={l.id} variant={form.list_ids.includes(l.id) ? 'default' : 'outline'}
                    className="cursor-pointer text-xs"
                    style={form.list_ids.includes(l.id) ? { backgroundColor: l.color } : {}}
                    onClick={() => toggleList(l.id)}>
                    {l.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {users.length > 0 && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1"><UserCircle size={12} /> {t('settings.assignTo')}</label>
              <div className="flex flex-wrap gap-1.5" data-testid="task-assign-select">
                {users.map(u => (
                  <button key={u.id} onClick={() => toggleAssignee(u.id)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border transition-all ${form.assignees.includes(u.id) ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                    <span className="w-4 h-4 rounded-full inline-flex items-center justify-center text-[8px] font-bold text-white" style={{ backgroundColor: u.avatar_color }}>{u.name?.[0]?.toUpperCase()}</span>
                    {u.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('task.priority')}</label>
            <div className="flex gap-2">
              {PRIORITIES.map(p => (
                <button key={p.value} data-testid={`priority-${p.value}`}
                  onClick={() => setForm(f => ({ ...f, priority: p.value }))}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${form.priority === p.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                  {t(p.label)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('task.dueDate')}</label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1 justify-start font-normal" data-testid="due-date-btn">
                    <CalendarBlank size={16} className="mr-2" />
                    {dueDate ? format(dueDate, 'PPP', { locale: dfnsLocale }) : t('task.none')}
                    {dueDate && <span role="button" tabIndex={0} className="ml-auto cursor-pointer" onClick={(e) => { e.stopPropagation(); setForm(f => ({ ...f, due_datetime: null })); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setForm(f => ({ ...f, due_datetime: null })); } }}><X size={14} /></span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={dueDate} onSelect={(d) => {
                    if (d) {
                      const iso = new Date(d.getFullYear(), d.getMonth(), d.getDate(), dueDate ? dueDate.getHours() : 9, dueDate ? dueDate.getMinutes() : 0, 0).toISOString();
                      setForm(f => ({ ...f, due_datetime: iso }));
                    }
                  }} />
                </PopoverContent>
              </Popover>
              {dueDate && (
                <Input
                  data-testid="due-time-input"
                  type="time"
                  className="w-28 shrink-0"
                  value={format(dueDate, 'HH:mm')}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(':').map(Number);
                    if (Number.isNaN(h)) return;
                    const d = new Date(dueDate);
                    d.setHours(h, m || 0, 0, 0);
                    setForm(f => ({ ...f, due_datetime: d.toISOString() }));
                  }}
                />
              )}
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('task.duration')}</label>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                data-testid="duration-input"
                type="number"
                min={1}
                step={durUnit === 1 ? 5 : 1}
                className="w-20"
                value={Math.max(1, Math.round(form.duration_minutes / durUnit))}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setForm(f => ({ ...f, duration_minutes: Number.isNaN(v) ? durUnit : Math.max(1, v) * durUnit }));
                }}
              />
              <Select value={String(durUnit)} onValueChange={(v) => {
                const unit = parseInt(v, 10);
                setDurUnit(unit);
                setForm(f => ({ ...f, duration_minutes: Math.max(1, Math.round(f.duration_minutes / unit)) * unit }));
              }}>
                <SelectTrigger className="w-24 h-9" data-testid="duration-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('task.minutes')}</SelectItem>
                  <SelectItem value="60">{t('task.hours')}</SelectItem>
                  <SelectItem value="1440">{t('task.days')}</SelectItem>
                </SelectContent>
              </Select>
              {[[15, '15m', 1], [60, '1h', 60], [120, '2h', 60], [1440, '1d', 1440]].map(([mins, label, unit]) => (
                <button key={mins} data-testid={`duration-${mins}`}
                  onClick={() => { setDurUnit(unit); setForm(f => ({ ...f, duration_minutes: mins })); }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${form.duration_minutes === mins ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm">{t('task.reference')}</span>
              <p className="text-xs text-muted-foreground">{t('task.referenceHint')}</p>
            </div>
            <Switch data-testid="toggle-task-reference" checked={form.is_reference} onCheckedChange={(v) => setForm(f => ({ ...f, is_reference: v }))} />
          </div>

          {(tags?.length > 0) && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('task.tags')}</label>
              {tags.length > 5 && (
                <Input
                  ref={tagSearchRef}
                  placeholder={t('task.filterTags')}
                  value={tagSearch}
                  onChange={e => setTagSearch(e.target.value)}
                  className="mb-2 h-7 text-xs"
                />
              )}
              <div className="flex flex-wrap gap-1.5">
                {filteredTags.map(tag => (
                  <Badge key={tag.id} data-testid={`tag-${tag.id}`}
                    variant={form.tags.includes(tag.id) ? 'default' : 'outline'}
                    className="cursor-pointer text-xs" style={form.tags.includes(tag.id) ? { backgroundColor: tag.color } : {}}
                    onClick={() => toggleTag(tag.id)}>
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {task?.id && boards.length > 0 && (
            <div data-testid="task-boards-section">
              <label className="text-xs text-muted-foreground mb-1 block">{t('task.boards')}</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {placements.map(p => {
                  const board = boards.find(b => b.id === p.board_id);
                  const col = columns.find(c => c.id === p.column_id);
                  return (
                    <Badge key={p.id} variant="secondary" className="text-xs gap-1" data-testid={`placement-${p.id}`}>
                      {board?.title || '?'} → {col?.title || '?'}
                      <button onClick={() => removePlacement(p)} className="hover:text-destructive"><X size={12} /></button>
                    </Badge>
                  );
                })}
              </div>
              {boards.some(b => !placements.some(p => p.board_id === b.id)) && (
                <Select value="" onValueChange={addToBoard}>
                  <SelectTrigger className="h-8 text-xs" data-testid="add-to-board-select">
                    <SelectValue placeholder={t('task.addToBoard')} />
                  </SelectTrigger>
                  <SelectContent>
                    {boards.filter(b => !placements.some(p => p.board_id === b.id)).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <Separator />

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-muted-foreground">{t('task.subtasks')}</label>
              <span className="text-xs text-muted-foreground">{subs.filter(s => s.completed).length}/{subs.length}</span>
            </div>
            <div className="space-y-1.5">
              {subs.map((sub, idx) => (
                <div key={sub.id || idx} className="flex items-center gap-2 group">
                  <Checkbox checked={sub.completed} onCheckedChange={() => toggleSubtask(idx)} />
                  <span className={`flex-1 text-sm ${sub.completed ? 'line-through text-muted-foreground' : ''}`}>{sub.title}</span>
                  <button className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" onClick={() => removeSubtask(idx)}>
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input data-testid="new-subtask-input" placeholder={t('task.addSubtask')} value={newSubtask} onChange={(e) => setNewSubtask(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSubtask()} className="text-sm" />
              <Button size="icon" variant="ghost" onClick={addSubtask} disabled={!newSubtask.trim()}><Plus size={16} /></Button>
            </div>
          </div>

          <Separator />

          <div>
            <button data-testid="recurrence-toggle"
              onClick={() => setShowRecurrence(!showRecurrence)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <Repeat size={16} /> {t('task.recurrence')}
              {showRecurrence && <Badge variant="secondary" className="text-xs">{t(`recurrence.${recForm.freq.toLowerCase()}`)}</Badge>}
            </button>
            {showRecurrence && (
              <div className="mt-3 p-3 rounded-lg bg-muted space-y-3">
                <div className="flex gap-2">
                  <Select value={recForm.freq} onValueChange={(v) => setRecForm(r => ({ ...r, freq: v }))}>
                    <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQ_OPTIONS.map(f => <SelectItem key={f} value={f}>{t(`recurrence.${f.toLowerCase()}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid="recurrence-interval" type="number" min={1} value={recForm.interval} onChange={(e) => setRecForm(r => ({ ...r, interval: parseInt(e.target.value) || 1 }))} className="w-20" />
                </div>
                {recForm.freq === 'WEEKLY' && (
                  <div className="flex gap-1">
                    {['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].map(d => (
                      <button key={d} onClick={() => toggleDay(d)}
                        className={`w-8 h-8 rounded-md text-xs font-medium transition-all ${recForm.byday.includes(d) ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-accent'}`}>
                        {t(`days.${d}`)}
                      </button>
                    ))}
                  </div>
                )}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">{t('recurrence.repeatUntil')}</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full justify-start font-normal text-xs" data-testid="recurrence-until-btn">
                        <CalendarBlank size={14} className="mr-2" />
                        {untilDate ? format(untilDate, 'PPP', { locale: dfnsLocale }) : t('recurrence.noEndDate')}
                        {untilDate && (
                          <button className="ml-auto" onClick={(e) => { e.stopPropagation(); setRecForm(r => ({ ...r, until: null })); }}>
                            <X size={12} />
                          </button>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={untilDate} onSelect={(d) => {
                        if (d) { const iso = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).toISOString(); setRecForm(r => ({ ...r, until: iso })); }
                      }} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button data-testid="task-save-btn" className="flex-1" onClick={handleSave} disabled={!form.title.trim()}>{t('common.save')}</Button>
            <Button data-testid="task-cancel-btn" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
